/**
 * narrationSegments — client-side parser that splits a single assistant
 * message into ordered narration / dialogue segments.
 *
 * Background: role plays can cast the AI as a *narrator* (a third actor)
 * that sets the scene and tells the learner what to say next, while also
 * voicing brief secondary characters. The backend keeps a single
 * `assistant` message role — the agent emits both voices in one turn,
 * demarcated with plain-text speaker labels:
 *
 *   Narrator: A gate agent walks up to the desk. Make the boarding call.
 *   [Gate agent]: Is this the line for flight 207?
 *
 * The mobile app parses those labels here and renders narration as a
 * distinct "stage-direction" block and dialogue as the normal AI bubble.
 * No wire-shape or message-model change — see
 * docs/specs/api/roleplay-narrator.api.md (ADR-024).
 *
 * NOTE: as of V1.6 the agent emits narrator turns via the structured
 * `narrate` directive (prose streams as text; the secondary character rides
 * in `stream_complete.directives.narrate`). `MessageBubble` prefers that path
 * via `segmentsFromNarrateDirective` (below). This label parser is retained
 * as the FALLBACK for streaming text (directives arrive only at completion)
 * and any pre-directive turns. See spec §2.5 (label path) and §2.7 (directive).
 *
 * Safety / backward-compat: segmented rendering is only used when at least
 * one `Narrator:` line is present (see `hasNarration`). Ordinary messages
 * (no labels) parse to a single dialogue segment and render exactly as
 * before. A stray `[x]:` line with no narrator line does NOT trigger
 * segmentation.
 */

export type NarrationSegmentKind = 'narration' | 'dialogue' | 'cue';

export interface NarrationSegment {
  /**
   * `narration` — the scene/atmosphere (centered, italic, muted).
   * `dialogue` — a character line (`[Name]:` or `character_*`), rendered as a bubble.
   * `cue` — the forward instruction telling the learner what to say next
   *   (structured `narrate` directive only; rendered as a distinct line).
   */
  kind: NarrationSegmentKind;
  /** Secondary-character name for labelled dialogue lines (`[Name]: …`). */
  speaker?: string;
  text: string;
}

// Line-anchored so a colon mid-sentence ("Sure: here's the plan") never matches.
const NARRATOR_RE = /^Narrator:\s?(.*)$/;
const SPEAKER_RE = /^\[([^\]]{1,40})\]:\s?(.*)$/;

/**
 * Insert the line break the model forgot: break before any `Narrator:` or
 * `[Name]:` label that got glued mid-line (e.g. `...position!"Narrator: ...`).
 * Without this the line-anchored splitter would leave the literal label inside
 * the text and render it verbatim. Markers already at a line start are left
 * alone. Runs before splitting so even sloppy model output renders cleanly.
 */
function normalizeMarkers(raw: string): string {
  return raw.replace(/([^\n])[ \t]*(Narrator:|\[[^\]\n]{1,40}\]:)/g, '$1\n$2');
}

/**
 * Split raw assistant text into ordered segments. Consecutive unlabelled
 * lines fold into the preceding segment (so multi-line narration or
 * dialogue stays one block). Empty segments are dropped.
 */
export function parseNarrationSegments(raw: string): NarrationSegment[] {
  if (!raw) return [];

  const segments: NarrationSegment[] = [];
  let current: NarrationSegment | null = null;

  for (const line of normalizeMarkers(raw).split('\n')) {
    const narration = NARRATOR_RE.exec(line);
    if (narration) {
      current = { kind: 'narration', text: narration[1] };
      segments.push(current);
      continue;
    }
    const speaker = SPEAKER_RE.exec(line);
    if (speaker) {
      current = { kind: 'dialogue', speaker: speaker[1].trim(), text: speaker[2] };
      segments.push(current);
      continue;
    }
    // Continuation of the current segment, or leading unlabelled text.
    if (current) {
      current.text = current.text.length ? `${current.text}\n${line}` : line;
    } else {
      current = { kind: 'dialogue', text: line };
      segments.push(current);
    }
  }

  for (const seg of segments) seg.text = seg.text.trim();
  return coalesce(segments.filter((seg) => seg.text.length > 0));
}

/**
 * Merge adjacent segments of the same voice into one block. Models often emit
 * several consecutive `Narrator:` lines in a turn; without this they'd render
 * as back-to-back narration bubbles. Same-speaker dialogue lines merge too;
 * different speakers stay separate.
 */
function coalesce(segments: NarrationSegment[]): NarrationSegment[] {
  const out: NarrationSegment[] = [];
  for (const seg of segments) {
    const prev = out[out.length - 1];
    if (prev && prev.kind === seg.kind && prev.speaker === seg.speaker) {
      prev.text = `${prev.text}\n${seg.text}`;
    } else {
      out.push({ ...seg });
    }
  }
  return out;
}

/**
 * Build segments from the structured narrator directives (the principled
 * replacement for label-parsing). The scene prose arrives as the streamed
 * message text; the `cue` and any secondary character ride in
 * `stream_complete.directives`. Three shapes, distinguished by where the
 * speaker sits relative to the scene:
 *   - `narrate` (Pattern A, learner initiates) → scene → cue, no character.
 *   - `narrate_reply` (Pattern B, character drives) → scene → speaker → cue.
 *     The speaker poses a question the cue answers, so it sits just before it.
 *   - `narrate_react` (Pattern C, character reacts to the learner's last
 *     line) → speaker → scene → cue. The reaction looks backward, so it sits
 *     at the TOP of the turn.
 * The `cue` is always last (nearest the input). Same `NarrationSegment[]`
 * shape the renderer consumes. Pass whichever directive is present; if more
 * than one is (shouldn't happen), precedence is react > reply > narrate.
 */
export function segmentsFromNarrateDirective(
  prose: string,
  directives: {
    narrate?: { cue?: string };
    narrate_reply?: { speaker_name?: string; speaker_line?: string; cue?: string };
    narrate_react?: { speaker_name?: string; reaction_line?: string; cue?: string };
  },
): NarrationSegment[] {
  const segments: NarrationSegment[] = [];
  const narration = prose.trim();
  const sceneSegment = narration ? { kind: 'narration' as const, text: narration } : null;

  const react = directives.narrate_react;
  if (react) {
    const speaker = react.speaker_name?.trim();
    const line = react.reaction_line?.trim();
    // Reaction first (it answers the learner's last line), then scene, then cue.
    if (speaker && line) segments.push({ kind: 'dialogue', speaker, text: line });
    if (sceneSegment) segments.push(sceneSegment);
    const cue = react.cue?.trim();
    if (cue) segments.push({ kind: 'cue', text: cue });
    return segments;
  }

  if (sceneSegment) segments.push(sceneSegment);

  const reply = directives.narrate_reply;
  if (reply) {
    const speaker = reply.speaker_name?.trim();
    const line = reply.speaker_line?.trim();
    if (speaker && line) segments.push({ kind: 'dialogue', speaker, text: line });
    const cue = reply.cue?.trim();
    if (cue) segments.push({ kind: 'cue', text: cue });
    return segments;
  }

  const cue = directives.narrate?.cue?.trim();
  if (cue) segments.push({ kind: 'cue', text: cue });
  return segments;
}

/** True when the text contains narrator markup worth segment-rendering. */
export function hasNarration(segments: NarrationSegment[]): boolean {
  return segments.some((seg) => seg.kind === 'narration');
}

/**
 * Whether to render this turn as distinct segments rather than one plain bubble.
 * True when there's narration OR a labeled secondary character (`[Name]:`). The
 * latter covers a turn that is *only* a named character speaking (e.g. the agent
 * answers a question the learner asked the colleague directly) — without this it
 * would fall through to the plain bubble and show the literal "[Name]:" prefix.
 * A normal role play (one unlabeled character) has neither, so it renders plainly.
 */
export function shouldSegment(segments: NarrationSegment[]): boolean {
  return segments.some(
    (seg) =>
      seg.kind === 'narration' ||
      seg.kind === 'cue' ||
      (seg.kind === 'dialogue' && !!seg.speaker),
  );
}

/**
 * The text that should be spoken by the tap-to-replay TTS button: only the
 * spoken dialogue, never the narrator's stage directions or the "[Name]:"
 * labels. Falls back to the full text when the turn isn't segmented.
 */
export function spokenDialogue(segments: NarrationSegment[], fullText: string): string {
  if (!shouldSegment(segments)) return fullText;
  return segments
    .filter((seg) => seg.kind === 'dialogue')
    .map((seg) => seg.text)
    .join('\n')
    .trim();
}
